import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import {
  getPromptTemplateCase,
  addTemplateToCase,
  removeTemplateFromCase,
  reorderTemplateInCase,
  updatePromptTemplateCase,
} from '../../api';
import type { PromptTemplateCase, PromptTemplateCaseAssignmentListing } from '../../api';
import { CaseFormModal, DeleteConfirmDialog, TemplatePickerModal } from '../index';

interface CaseDetailModalProps {
  open: boolean;
  onClose: () => void;
  caseId: string | null;
  onCaseUpdated: () => void;
}

export default function CaseDetailModal({
  open,
  onClose,
  caseId,
  onCaseUpdated,
}: CaseDetailModalProps) {
  const [caseData, setCaseData] = useState<PromptTemplateCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplateCaseAssignmentListing | null>(
    null
  );

  const loadCase = useCallback(async () => {
    if (!caseId || !open) return;
    setLoading(true);
    try {
      const data = await getPromptTemplateCase(caseId);
      setCaseData(data);
    } catch {
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  }, [caseId, open]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  const handleEditSubmit = useCallback(
    async (form: { title: string; description?: string; role_ids: string[] }) => {
      if (!caseId) return;
      await updatePromptTemplateCase(caseId, form);
      await loadCase();
      onCaseUpdated();
      setEditModalOpen(false);
    },
    [caseId, loadCase, onCaseUpdated]
  );

  const handleAddTemplate = useCallback(
    async (templateId: string) => {
      if (!caseId) return;
      await addTemplateToCase(caseId, templateId);
      await loadCase();
      onCaseUpdated();
    },
    [caseId, loadCase, onCaseUpdated]
  );

  const handleRemoveTemplate = useCallback(
    async (templateId: string) => {
      if (!caseId) return;
      await removeTemplateFromCase(caseId, templateId);
      await loadCase();
      onCaseUpdated();
      setDeleteTarget(null);
    },
    [caseId, loadCase, onCaseUpdated]
  );

  const handleMoveUp = useCallback(
    async (item: PromptTemplateCaseAssignmentListing) => {
      if (!caseId || !caseData) return;
      const idx = caseData.prompt_templates.findIndex((t) => t.template_id === item.template_id);
      if (idx <= 0) return;
      const newOrder = idx - 1;
      await reorderTemplateInCase(caseId, item.template_id, newOrder);
      await loadCase();
      onCaseUpdated();
    },
    [caseId, caseData, loadCase, onCaseUpdated]
  );

  const handleMoveDown = useCallback(
    async (item: PromptTemplateCaseAssignmentListing) => {
      if (!caseId || !caseData) return;
      const idx = caseData.prompt_templates.findIndex((t) => t.template_id === item.template_id);
      if (idx < 0 || idx >= caseData.prompt_templates.length - 1) return;
      const newOrder = idx + 1;
      await reorderTemplateInCase(caseId, item.template_id, newOrder);
      await loadCase();
      onCaseUpdated();
    },
    [caseId, caseData, loadCase, onCaseUpdated]
  );

  const handleClose = useCallback(() => {
    if (!loading) {
      setCaseData(null);
      onClose();
    }
  }, [loading, onClose]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <span>{caseData?.title ?? 'Кейс'}</span>
            <IconButton size="small" onClick={() => setEditModalOpen(true)} title="Редактировать">
              <EditIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {caseData?.description && (
                <Box mb={2} fontSize={14} color="text.secondary">
                  {caseData.description}
                </Box>
              )}
              <Box mb={2}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddTemplateOpen(true)}
                >
                  Добавить промпт
                </Button>
              </Box>
              <List dense>
                {caseData?.prompt_templates?.length ? (
                  caseData.prompt_templates.map((item, idx) => (
                    <ListItem
                      key={item.template_id}
                      secondaryAction={
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveUp(item)}
                            disabled={idx === 0}
                            title="Поднять"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveDown(item)}
                            disabled={idx === caseData.prompt_templates.length - 1}
                            title="Опустить"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTarget(item)}
                            title="Удалить из кейса"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText primary={`${idx + 1}. ${item.title}`} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Шаблоны не добавлены"
                      secondary="Нажмите «Добавить шаблон»"
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <CaseFormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        caseItem={caseData ?? undefined}
      />

      <TemplatePickerModal
        open={addTemplateOpen}
        onClose={() => setAddTemplateOpen(false)}
        onSelect={handleAddTemplate}
        excludeIds={caseData?.prompt_templates?.map((t) => t.template_id) ?? []}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await handleRemoveTemplate(deleteTarget.template_id);
        }}
        title="Удалить шаблон из кейса?"
        message={deleteTarget ? `Шаблон «${deleteTarget.title}» будет удалён из кейса.` : ''}
      />
    </>
  );
}
